import { DataTable } from "primereact/datatable";
import PageContent from "../../components/PageContent";
import PageTitle from "../../components/PageTitle";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import useApi from "../../hooks/ApiHook";
import { InputText } from "primereact/inputtext";
import useToast from "../../hooks/ToastHook";
import { useNavigate, useParams } from "react-router-dom";
import InputSearchSidebar from "../../components/InputSearchSidebar";
import Loading from "../../components/Loading";
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

const currency = Intl.NumberFormat('pt-br', { style: 'currency', currency: 'BRL' });

export default function() {

    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { id } = useParams();
    const [ products, setProducts ] = useState([]);
    const [ selectedToEdit, setSelectedToEdit ] = useState();
    const [ selectProductsVisible, setSelectProductsVisible ] = useState(false);
    const [ description, setDescription ] = useState('');
    const [ saving, setSaving ] = useState(false);
    const [ loading, setLoading ] = useState(true);
    const isNew = id === 'new';

    useEffect(() => {
        if (isNew) {
            setLoading(false);
            return;
        }
        setLoading(true);
        api.get('price-tables/' + id).then(({ data }) => {
            setDescription(data.description);
            const products = data.items.map(i => ({ ...i.product, price: i.price, unit: i.unit }))
            setProducts(products);
        }).finally(() => setLoading(false));
    }, [ id ]);

    useEffect(() => {
        if (selectedToEdit) {
            setSelectedToEdit(products.find(i => i.id === selectedToEdit.id));
        }
    }, [products]);

    const putProduct = (product) => {
        const index = products.findIndex(i => i.id === product.id);
        products[index] = product;
        setProducts([ ...products ]);
    }

    const removeProduct = (product) => {
        setProducts(products.filter(i => i.id !== product.id));
    }

    const changeUnit = product => {
        const units = product.units;
        const actual = product.unit;
        const index = product.units.findIndex(i => i === actual);
        putProduct({ ...product, unit: units[index + 1 >= units.length ? 0 : index + 1] })
    }

    const onSelectProduct = product => {
        if (products.some(p => p.id === product.id)) {
            return;
        }
        setProducts([ ...products, { ...product } ])
    }

    const save = () => {
        setSaving(true);

        const productsPayload = products.map(p => ({
            product: { id: p.id },
            price: p.price,
            unit: p.unit
        }));

        const payload = {
            description: description,
            items: productsPayload
        }

        const request = isNew 
            ? api.post('price-tables', payload)
            : api.put(`price-tables/${id}`, payload);

        request.then(() => {
            toast.showSuccess('Tabela de preçoes criada com sucesso');
            navigate(-1);
        }).catch(e => {
            toast.showAxiosError(e);
        }).finally(() => {
            setSaving(false);
        });
    }

    const quantityEditor = product => (
        <div className="p-inputgroup">
            <InputNumber 
                value={product.price} 
                onChange={e => putProduct({ ...product, price: (e.value || 0) })}
                maxFractionDigits={2}
                mode="currency"
                currency="BRL"
                locale="pt-BR"/>
            <span className="p-inputgroup-addon" onClick={() => changeUnit(product)}>
                /{product.unit}
            </span>
        </div>
    );

    const productActions = product => (
        <div>
            <Button icon="pi pi-trash" title="Remover" onClick={() => removeProduct(product)} tabIndex={-1}/>
            <Button icon="pi pi-pencil" title="Editar" className="ml-2 md:hidden" onClick={() => setSelectedToEdit(product)}/>
        </div>
    );

    if (loading) return (
        <Loading/>
    );

    return (
        <div>
            <PageTitle title="Tabela de preço"/>
            <PageContent>
                <div className="flex justify-content-between flex-wrap gap-3 mb-3">
                    <div>
                        <InputText
                            value={description}
                            placeholder="Descrição"
                            onChange={e => setDescription(e.target.value)}/>
                    </div>
                    <div>
                        <Button label="Selecionar produtos" className="mr-2" onClick={() => setSelectProductsVisible(true)}/>
                        <Button label="Finalizar" onClick={save} loading={saving}/>
                    </div>
                </div>
                <DataTable value={products} selectionMode="single" emptyMessage="Adicione produtos ao pedido" responsiveLayout="stack">
                    <Column field="description" header="Produto"/>
                    <Column body={quantityEditor} header="Valor" style={{width:'20%'}} className="md:table-cell" bodyStyle={{ display: 'none' }}/>
                    <Column body={p => currency.format(p.price || 0) + '/' + p.unit} header="Valor" className="md:hidden"/>
                    <Column body={productActions} header="Ações" align='right'></Column>
                </DataTable>
            </PageContent>
            <Sidebar visible={selectedToEdit} onHide={() => setSelectedToEdit(null)} position="right">
                <h2>Quantidade</h2>
                <div className="p-inputgroup">
                    <InputNumber 
                        value={selectedToEdit?.price} 
                        onChange={e => putProduct({ ...selectedToEdit, price: (e.value || 0) })}
                        maxFractionDigits={2}
                        mode="currency"
                        currency="BRL"
                        locale="pt-BR"/>
                    <span className="p-inputgroup-addon" onClick={() => changeUnit(selectedToEdit)}>
                        {selectedToEdit?.unit}
                    </span>
                </div>
                <Button label="Fechar" className="w-full mt-2" onClick={() => setSelectedToEdit(null)}/>
            </Sidebar>
            <ProductSelection 
                visible={selectProductsVisible}
                selectedProducts={products}
                onHide={() => setSelectProductsVisible(false)}
                onSelect={onSelectProduct}/>
        </div>
    );
}

function ProductSelection({ onSelect, selectedProducts, visible, onHide }) {

    const api = useApi();
    const toast = useToast();
    const [ productList, setProductList ] = useState([]);
    const [ searchTerm, setSearchTerm ] = useState('');
    const [ loading, setLoading ] = useState(false);

    useEffect(() => {
        setLoading(true);
        const timeOutId = setTimeout(() => {
            api.get('products', { params: { 'description[includes]': searchTerm } }).then(r => {
                setProductList(r.data.data);
            }).catch(e => {
                toast.showAxiosError(e);
            }).finally(() => {
                setLoading(false);
            });
        }, 500);
        return () => clearTimeout(timeOutId);
    }, [searchTerm]);

    return (
        <Sidebar visible={visible} onHide={onHide} position="right" className="md:w-30rem">
            <div className="h-full flex flex-column">
                <h2>Selecionar produtos</h2>
                <IconField iconPosition="left">
                    {loading ? <InputIcon className="pi pi-spin pi-spinner"/> : <InputIcon className="pi pi-search"/>}
                    <InputText
                        className="w-full" 
                        onChange={e => setSearchTerm(e.target.value)} 
                        value={searchTerm}/>
                </IconField>
                <div className="w-full flex-grow-1 mt-4 max-w-full flex-shrink-1 overflow-y-hidden overflow-y-scroll">
                    {productList.map(p => {
                        const selected = selectedProducts.some(s => s.id === p.id);
                        return (
                            <div className={`p-3 cursor-pointer hover:surface-hover ${selected && 'bg-primary-reverse'}`} onClick={() => onSelect(p)}>
                                <div className="font-semibold">{p.description}</div>
                                <div className="text-sm" style={{ maxWidth: 300, whiteSpace: 'break-spaces' }}>{p.eanCode}</div>
                                <div className="text-sm" style={{ maxWidth: 300, whiteSpace: 'break-spaces' }}>{p.quantity} {p.unit}</div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </Sidebar>
    );
}